/* eslint-disable max-lines */
// TODO: Break this file into smaller files -FF
//deps
import React, { memo, useState, useEffect, useCallback, useRef, useMemo } from "react";

//constants
import { PAYMENT_HEADER_THEME, PROCEED_TOPAY_TEXT, SCROLL_POSITION } from "@dineout-cart-client/constants/index";

//helpers
import { getBillDetailData, getTipAmount, isPaymentFlowScanQR } from "@dineout-cart-client/helpers/cartHelper";
import { CartTransformer } from "@dineout-cart-client/helpers/cartTransformer";

//internal deps
import {
    closeWebview,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    useSessionStorage,
    recordNREvent,
    NEWRELIC_TABLES,
} from "@swiggy-private/checkout-core";
import {
    checkIfDiscountExists,
    CouponSection,
    PaymentCartHeader,
    SettleBillFooter,
    RewardsWidget,
    AlertBanner,
    DineCashPoolingWidget,
} from "@swiggy-private/dineout-cart-ui";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { TurboPayment } from "@swiggy-private/payment-client";
import { TurboPaymentWidgetShimmerV2 } from "@swiggy-private/payment-ui";
import {
    BillDetails,
    currencyFormat,
    getBillEntriesList,
    BILL_ELEMENT_ENTRIES,
    openHelpWebView,
} from "@swiggy-private/dineout-payment-success-ui";

//styles
import {
    ButtonTextContainer,
    Container,
    ContentContainer,
    CTAContainer,
    GlobalStyle,
    ProceedCTA,
    StyledText,
    BillContainer,
    CenterContainer,
} from "./styles";

//analytics
import {
    useBillPaymentAnalytics,
    useDineoutBookingAnalytics,
} from "@dineout-cart-client/hooks/useDineoutCartAnalytics";
// TODO: get the interface in the file and check for further refactoring.
import { IDineoutPaymentCart } from "@dineout-cart-client/interfaces";
import { getApplyCouponWidget, isCouponApplied } from "@dineout-cart-client/utils/Coupon";
import {
    getIsRewardRedeemed,
    getRedeemableRewards,
    getRewardsTogglePayload,
} from "@dineout-cart-client/utils/RewardsWidgetUtils";
import { BILL_CART_ONLOAD_DISCOUNT_LOTTIE_SHOWN } from "@dineout-cart-client/constants/StorageKeys";
import { createAndPersistPaymentLink } from "@dineout-cart-client/helpers/PaymentLink";
import { isTurboEnabled } from "@dineout-cart-client/helpers/TurboPayment";

const FLOW_BILL_PAYMENT = "BillPayment";
const PAGE_DO_WEBVIEW = "do-webview";
export const DineoutPaymentCart: React.FC<IDineoutPaymentCart> = (props: IDineoutPaymentCart) => {
    const {
        enteredAmount,
        distanceBasedAlert,
        onClickApplyCoupon,
        onClickRemoveCoupon,
        onProceedToPay,
        billToPay,
        doCart,
        onUpdateTip,
        onSettleBillPress,
        onRedeemCash,
        scanAgainEventPayload,
        uniqueIdForCart,
        dineCashPoolingWidgetData,
        onDineCashPoolingClick,
        handlePaymentAlertAction,
        onTurboPayClick,
    } = props;

    const {
        onImpressionPayButton,
        onImpressionDistanceAlert,
        onImpressionBillDetails,
        onTipViewed,
        onHelpClicked,
        onClickTurboPaymentMethodChange,
        onImpressionTurboCheckout,
        onClickTurboCheckout,
        onImpressionDinecashPoolWidget,
        onClickDinecashPoolWidget,
    } = useBillPaymentAnalytics();
    const { onImpressionCashBackEarned, onImpressionCashBackToggle, onClickCashBackToggle } =
        useDineoutBookingAnalytics();
    const [showCouponSuccessLottie, setShowCouponSuccessLottie] = useState(false);
    const [isTurboPayWidgetHidden, setIsTurboPayWidgetHidden] = useState(false);
    const transformedCart = useMemo(() => CartTransformer(doCart), [doCart]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [onLoadDiscountLottieShown, setOnLoadDiscountLottieShown] = useSessionStorage(
        BILL_CART_ONLOAD_DISCOUNT_LOTTIE_SHOWN,
        false,
    );
    const billSectionInfo = useMemo(
        () => getBillDetailData(transformedCart, enteredAmount),
        [transformedCart, enteredAmount],
    );
    const applyCouponWidget = useMemo(() => getApplyCouponWidget(doCart), [doCart]);
    const [stickHeader, setStickHeader] = useState<boolean>(false);
    const headerWidget = useMemo(
        () => transformedCart?.headerWidget?.widgetInfo,
        [transformedCart?.headerWidget?.widgetInfo],
    );
    const restoInfo = useMemo(() => transformedCart?.storesInfo?.[0], [transformedCart?.storesInfo]);
    const tipAmount = useMemo(() => getTipAmount(doCart?.bill?.charges), [doCart?.bill?.charges]);
    const rewardWidget = useMemo(() => doCart?.rewardsWidget, [doCart?.rewardsWidget]);
    const _isTurboEnabled = isTurboEnabled(doCart?.hideTurboCheckoutOption);
    const isRewardRedeemed = useMemo(() => getIsRewardRedeemed(rewardWidget), [rewardWidget]);
    const redeemableRewards = useMemo(() => getRedeemableRewards(rewardWidget), [rewardWidget]);
    const [paymentUniqueId, setPaymentUniqueId] = useState("");
    const resetCouponSuccessLottie = useCallback(() => {
        setShowCouponSuccessLottie(false);
    }, []);

    useEffectOnce(() => {
        const showDiscountLottieForInitialLoad =
            !onLoadDiscountLottieShown &&
            checkIfDiscountExists(
                enteredAmount,
                String(transformedCart?.bill?.dineoutPricingInfo?.totalBillExcludingExtras || 0),
                headerWidget?.savingsText,
            );
        if (showDiscountLottieForInitialLoad || isCouponApplied(applyCouponWidget)) {
            setOnLoadDiscountLottieShown(true);
            setShowCouponSuccessLottie(true);
        }
    });

    const onErrorTurboPay = useCallback(() => {
        setIsTurboPayWidgetHidden(true);
        recordNREvent({
            eventType: NEWRELIC_TABLES.TURBO_PAY_WIDGET_HIDDEN_DUE_TO_ERROR,
            isTurboEnabled: String(_isTurboEnabled),
        });
    }, [_isTurboEnabled]);

    const showSettleBill = useMemo(() => !isEmpty(transformedCart?.footer), [transformedCart?.footer]);

    useEffect(() => {
        if (showSettleBill) return;
        if (_isTurboEnabled && !isEmpty(uniqueIdForCart)) {
            setPaymentUniqueId(createAndPersistPaymentLink());
            try {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    eventSubType: "turbo_payment_cart_onload",
                    isTurboEnabled: String(_isTurboEnabled),
                    type: "SERVICELINE",
                });
            } catch (error) {
                /* No-op*/
            }
        }
    }, [showSettleBill, _isTurboEnabled, uniqueIdForCart]);

    const goBack = useCallback(() => {
        closeWebview();
    }, []);

    const onHelpClick = useCallback(() => {
        onHelpClicked();
        openHelpWebView({ orderId: "", restaurantId: String(restoInfo?.id) });
    }, [onHelpClicked, restoInfo?.id]);

    const onToggleSwitch = useCallback(() => {
        onClickCashBackToggle({
            eventId: doCart?.cartId || "",
            dealCategory: doCart?.type || "",
            restaurantId: Number(restoInfo?.id) || 0,
            billAmount: Number(transformedCart?.bill?.dineoutPricingInfo?.billToPay) || 0,
            ...getRewardsTogglePayload(rewardWidget),
            toggleState: isRewardRedeemed ? "off" : "on",
            cashbackPerc: transformedCart?.bill?.dineoutPricingInfo?.totalDiscountPercentage || 0,
            couponApplied: Boolean(applyCouponWidget?.coupon),
            couponDiscount: transformedCart?.bill?.dineoutPricingInfo?.totalDiscount || 0,
            couponDisabled: !applyCouponWidget || String(doCart?.showCouponWidget) === "false",
        });
        onRedeemCash(!isRewardRedeemed, redeemableRewards);
    }, [
        doCart?.cartId,
        doCart?.type,
        restoInfo?.id,
        transformedCart?.bill?.dineoutPricingInfo,
        rewardWidget,
        isRewardRedeemed,
        onClickCashBackToggle,
        onRedeemCash,
        redeemableRewards,
        applyCouponWidget,
        doCart?.showCouponWidget,
    ]);

    const handlePayBillImpression = useCallback(() => {
        const startingBillValue = transformedCart?.bill?.dineoutPricingInfo?.billToPay;
        const restaurantID = restoInfo?.id;
        const sourceScreenValue = "";
        const { paybillSource, paymentFlow } = scanAgainEventPayload;

        const payBillBtnInfo = {
            restaurantID: restaurantID,
            startingBillValue: startingBillValue,
            sourceScreenValue: sourceScreenValue,
            flow: FLOW_BILL_PAYMENT,
            page: PAGE_DO_WEBVIEW,
        };

        if (isPaymentFlowScanQR(paymentFlow)) {
            onImpressionPayButton({
                ...payBillBtnInfo,
                paybillSource,
                paymentFlow,
            });
        } else {
            onImpressionPayButton(payBillBtnInfo);
        }
    }, [
        transformedCart?.bill?.dineoutPricingInfo?.billToPay,
        restoInfo?.id,
        scanAgainEventPayload,
        onImpressionPayButton,
    ]);

    const turboEventPayload = useMemo(
        () => ({
            restaurantID: restoInfo?.id,
            startingBillValue: transformedCart?.bill?.dineoutPricingInfo?.billToPay,
            sourceScreenValue: "",
            flow: FLOW_BILL_PAYMENT,
            page: PAGE_DO_WEBVIEW,
            buttonVariant: "button",
        }),
        [restoInfo?.id, transformedCart?.bill?.dineoutPricingInfo?.billToPay],
    );

    const handleTurboCheckoutImpression = useCallback(
        (_paymentMethod: string, _billAmount: string, _fromFallback?: boolean, groupName?: string) => {
            if (_isTurboEnabled) {
                onImpressionTurboCheckout({ ...turboEventPayload, paymentType: groupName });
            }
        },
        [onImpressionTurboCheckout, _isTurboEnabled, turboEventPayload],
    );

    const onPayClickTurboCheckout = useCallback(
        (_paymentMethod: string, _billAmount: string, groupName?: string) => {
            if (_isTurboEnabled) {
                onClickTurboCheckout({ ...turboEventPayload, paymentType: groupName });
            }

            // stop dinecash pooling for turbo pay checkout
            onTurboPayClick?.();
        },
        [onClickTurboCheckout, _isTurboEnabled, turboEventPayload, onTurboPayClick],
    );

    const onPayClickTurboPaymentMethodChange = useCallback(
        (_paymentMethod: string, _billAmount: string, _fromFallback?: boolean, groupName?: string) => {
            if (_isTurboEnabled) {
                onClickTurboPaymentMethodChange({ ...turboEventPayload, paymentType: groupName });
            }
        },
        [onClickTurboPaymentMethodChange, _isTurboEnabled, turboEventPayload],
    );

    const handleDistanceAlertImpression = useCallback(() => {
        if (distanceBasedAlert) {
            const lat = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat);
            const lng = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng);

            /* eslint-disable @typescript-eslint/naming-convention */
            const locAlertContext = {
                cust_location: `${lat}, ${lng}`,
                rest_id: restoInfo?.id,
                rest_location: restoInfo?.restaurantLatLong,
                distanceBasedAlert: distanceBasedAlert,
            };

            onImpressionDistanceAlert(locAlertContext);
        }
    }, [distanceBasedAlert, restoInfo, onImpressionDistanceAlert]);

    const handleBillDetailsImpression = useCallback(() => {
        if (billSectionInfo) {
            const billDetails = getBillEntriesList(billSectionInfo);
            const { paybillSource, paymentFlow } = scanAgainEventPayload;
            const billDetailsContext = {
                restaurantID: String(restoInfo.id || ""),
                offerId: "",
                dealCategory: "",
                guests: "",
                slotDetails: "",
                billDetails,
            };

            if (isPaymentFlowScanQR(paymentFlow)) {
                onImpressionBillDetails({
                    ...billDetailsContext,
                    paybillSource,
                    paymentFlow,
                });
            } else {
                onImpressionBillDetails(billDetailsContext);
            }
        }
    }, [billSectionInfo, restoInfo, onImpressionBillDetails, scanAgainEventPayload]);

    useEffect(() => {
        handlePayBillImpression();
        handleDistanceAlertImpression();
        handleBillDetailsImpression();
        onTipViewed();
    }, [handlePayBillImpression, handleDistanceAlertImpression, handleBillDetailsImpression, onTipViewed]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollYPosition = scrollContainerRef.current?.scrollTop || 0;
            setStickHeader(scrollYPosition > SCROLL_POSITION);
        };

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
        }

        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener("scroll", handleScroll);
            }
        };
    }, []);
    const savingText = transformedCart?.savingsText;
    const restoLocation = `${restoInfo.landmark} ${restoInfo.displayAreaName}, ${restoInfo.cityName}`;
    const proceedToPayText = `${PROCEED_TOPAY_TEXT} ${BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL}${currencyFormat(
        transformedCart?.bill?.dineoutPricingInfo?.billToPay || 0,
    )}`;

    const shouldShowTurboPayment = Boolean(paymentUniqueId);

    useEffect(() => {
        if (shouldShowTurboPayment) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_PAY_WIDGET_SHOWN,
                isTurboEnabled: String(_isTurboEnabled),
            });
        }
    }, [shouldShowTurboPayment, _isTurboEnabled]);

    const dinecashPoolWidgetAnalyticsContext = useMemo(
        () => ({
            text: dineCashPoolingWidgetData?.title || "",
            subtext: dineCashPoolingWidgetData?.subtitle || "",
            amt_received: dineCashPoolingWidgetData?.pooledAmount ?? "",
            max_contribution_limit_reached:
                (dineCashPoolingWidgetData?.totalPledgedAmount ?? 0) >=
                (dineCashPoolingWidgetData?.maxPoolAmountAllowed ?? 0),
            max_contributors_limit_reached:
                (dineCashPoolingWidgetData?.noOfCurrentContributors ?? 0) >=
                (dineCashPoolingWidgetData?.maxNoOfContributor ?? 0),
            type: dineCashPoolingWidgetData?.type || "",
            tooltip_text: dineCashPoolingWidgetData?.toolTip?.text || "",
            widget_state: dineCashPoolingWidgetData?.state || "",
        }),
        [dineCashPoolingWidgetData],
    );

    const paymentAlertConfig = useMemo(
        () => ({
            hideCloseIcon: false,
            hideOnOverlayClick: false,
            primaryAction: { onClick: handlePaymentAlertAction },
            // as per requirement, onClose also need to call same function to refresh the cart
            onClose: handlePaymentAlertAction,
        }),
        [handlePaymentAlertAction],
    );

    const dinecashAnalytics = useMemo(
        () => ({
            onWidgetImpression: () => {
                onImpressionDinecashPoolWidget(dinecashPoolWidgetAnalyticsContext);
            },
            onWidgetClick: () => {
                onClickDinecashPoolWidget(dinecashPoolWidgetAnalyticsContext);
            },
        }),
        [dinecashPoolWidgetAnalyticsContext, onImpressionDinecashPoolWidget, onClickDinecashPoolWidget],
    );

    return (
        <Container ref={scrollContainerRef}>
            <GlobalStyle />
            <ContentContainer addMargin={Boolean(transformedCart?.footer)}>
                <PaymentCartHeader
                    savingContainerExists={Boolean(savingText)}
                    mastheadBackgroundImage={headerWidget?.mastheadBackgroundImage}
                    tickerTextColor={headerWidget?.tickerTextColor}
                    helpIconImage={headerWidget?.helpIconImage}
                    savingsImage={headerWidget?.savingsImage}
                    savingsText={headerWidget?.savingsText}
                    rewardText={headerWidget?.rewardsText}
                    mastheadRewardsBackgroundImage={headerWidget?.mastheadRewardsBackgroundImage}
                    popupSavingsBackgroundImage={headerWidget?.popupSavingsBackgroundImage}
                    restaurantName={restoInfo?.name}
                    restaurantLocation={restoLocation}
                    title={headerWidget?.title}
                    enteredAmount={enteredAmount}
                    billToPay={billToPay}
                    totalBillExcludingExtras={transformedCart?.bill?.dineoutPricingInfo?.totalBillExcludingExtras || 0}
                    isStickHeader={stickHeader}
                    headerTheme={stickHeader ? PAYMENT_HEADER_THEME.LIGHT : PAYMENT_HEADER_THEME.DARK}
                    dineoutMastHeadTextWidget={headerWidget?.dineoutMastHeadTextWidget}
                    goBack={goBack}
                    onHelpClick={onHelpClick}
                    showCouponSuccessLottie={showCouponSuccessLottie}
                    resetCouponSuccessLottie={resetCouponSuccessLottie}
                    isRewardRedeemed={isRewardRedeemed}
                    redeemableRewards={redeemableRewards}
                ></PaymentCartHeader>
                {!isEmpty(rewardWidget) && (
                    <RewardsWidget
                        rewardsWidget={rewardWidget}
                        onToggleSwitch={onToggleSwitch}
                        isSelected={isRewardRedeemed}
                        eventId={doCart?.cartId || ""}
                        dealCategory={doCart?.type || ""}
                        restaurantId={Number(restoInfo?.id) || 0}
                        billAmount={Number(transformedCart?.bill?.dineoutPricingInfo?.billToPay) || 0}
                        cashbackPerc={transformedCart?.bill?.dineoutPricingInfo?.totalDiscountPercentage || 0}
                        couponApplied={Boolean(applyCouponWidget?.coupon)}
                        couponDiscount={transformedCart?.bill?.dineoutPricingInfo?.totalDiscount || 0}
                        onImpressionCashBackEarned={onImpressionCashBackEarned}
                        onImpressionCashBackToggle={onImpressionCashBackToggle}
                    />
                )}
                {!isEmpty(dineCashPoolingWidgetData) && (
                    <DineCashPoolingWidget
                        widgetData={dineCashPoolingWidgetData}
                        onPress={onDineCashPoolingClick}
                        analytics={dinecashAnalytics}
                    />
                )}
                <div>
                    <CenterContainer>
                        {applyCouponWidget && (
                            <CouponSection
                                subtitle={applyCouponWidget.subtitle}
                                title={applyCouponWidget.title}
                                subtitleLogo={applyCouponWidget.subtitleLogo}
                                subtitleColour={applyCouponWidget.subtitleColour}
                                appliedCoupon={applyCouponWidget.coupon}
                                appliedCouponValue={transformedCart?.bill?.dineoutPricingInfo?.totalDiscount}
                                onClickApplyCoupon={onClickApplyCoupon}
                                onClickRemoveCoupon={onClickRemoveCoupon}
                            ></CouponSection>
                        )}
                        {billSectionInfo ? (
                            <BillContainer>
                                <BillDetails
                                    billEntries={billSectionInfo}
                                    isCart
                                    addTip={true}
                                    updateTip={onUpdateTip}
                                    title="Bill Details"
                                    maxTipAmount={enteredAmount - 1}
                                    tipAmountExists={tipAmount || 0}
                                />
                            </BillContainer>
                        ) : null}
                    </CenterContainer>
                </div>
            </ContentContainer>
            <CTAContainer showTurboPayment={_isTurboEnabled && !isTurboPayWidgetHidden} showSettleBill={showSettleBill}>
                {showSettleBill && (
                    <SettleBillFooter
                        footer={transformedCart?.footer}
                        goBack={goBack}
                        onSettleBillPress={onSettleBillPress}
                    ></SettleBillFooter>
                )}

                {!showSettleBill && _isTurboEnabled && !isTurboPayWidgetHidden && (
                    <>
                        {distanceBasedAlert && distanceBasedAlert != "null" && paymentUniqueId && (
                            <AlertBanner
                                message={distanceBasedAlert}
                                textColor="highlight"
                                backgroundColor={"highlight_Sub"}
                                isTurboVariantEnabled={_isTurboEnabled}
                                showIcon={true}
                                iconFillColor="highlight"
                            />
                        )}
                        {paymentUniqueId ? (
                            <TurboPayment
                                uniqueId={paymentUniqueId}
                                onPaymentMethodChange={onProceedToPay}
                                onError={onErrorTurboPay}
                                showLoader={false}
                                showDiscountedValueOnSlider={false}
                                isTurboV2={true}
                                hideEducationComms={false}
                                onTurboPayClickEvent={onPayClickTurboCheckout}
                                onPaymentMethodChangeClickEvent={onPayClickTurboPaymentMethodChange}
                                onTurboPayImpressionEvent={handleTurboCheckoutImpression}
                                paymentAlertConfig={paymentAlertConfig}
                            />
                        ) : (
                            <TurboPaymentWidgetShimmerV2 />
                        )}
                    </>
                )}

                {!showSettleBill && (!_isTurboEnabled || isTurboPayWidgetHidden) && (
                    <>
                        {distanceBasedAlert && distanceBasedAlert != "null" && (
                            <AlertBanner
                                message={distanceBasedAlert}
                                textColor="highlight"
                                backgroundColor={"highlight_Sub"}
                            />
                        )}
                        <ProceedCTA data-testid={"ProceedToPayDoCart"} onClick={onProceedToPay}>
                            <ButtonTextContainer>
                                <StyledText variant={"V2.subheading1.neutral.bold"} color={"background_Primary"}>
                                    {proceedToPayText}
                                </StyledText>
                            </ButtonTextContainer>
                        </ProceedCTA>
                    </>
                )}
            </CTAContainer>
        </Container>
    );
};
export default memo(DineoutPaymentCart);
